// Listens for the global PWA events dispatched by lib/sw.ts and lib/api.ts:
//
//   sw:update-available — a new service worker is installed and waiting
//   queue:enqueued      — a mutation got saved to the offline queue
//   queue:drained       — the queue replayed (online came back)
//
// Surfaces them as Toasts. Rendered once at the AppShell level so the
// authed path always has them; the landing page doesn't need them.
//
// We also keep an "Update ready" banner pinned at the top of the
// viewport when a new SW is waiting — the toast disappears after 4s
// and users need a persistent affordance to act.

import { useEffect, useState } from 'react';
import { useToast } from '../ui/Toast';
import { applyUpdate } from '../../lib/sw';

export function PwaToasts() {
  const toast = useToast();
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    function onUpdate() {
      setUpdateReady(true);
      toast.push('New version available — tap "Reload" at the top of the screen.');
    }
    function onEnqueued() {
      toast.push("Saved offline — we'll sync when you're back online");
    }
    function onDrained(e: Event) {
      const ev = e as CustomEvent<{ ok: number; failed: number; dropped: number; deferred: number }>;
      const { ok, failed, dropped, deferred } = ev.detail
        ?? { ok: 0, failed: 0, dropped: 0, deferred: 0 };
      if (ok > 0) toast.push(`Synced ${ok} change${ok === 1 ? '' : 's'}`);
      if (dropped > 0) {
        toast.push(
          `${dropped} queued change${dropped === 1 ? '' : 's'} were too old to sync`,
        );
      }
      // Genuinely terminal 4xx (400/403/422 etc) — the queued item was
      // discarded because replaying will never succeed. User-visible so
      // they know to re-enter the data if it mattered.
      if (failed > 0) {
        toast.push(
          `${failed} queued change${failed === 1 ? '' : 's'} couldn't be saved — please re-enter`,
        );
      }
      // Transient defer (401/429/5xx/network). NOT lost — will retry on
      // next foreground / online event / 60s poll. Quiet status, no
      // alarm; user just sees something is in flight.
      if (deferred > 0 && ok === 0 && dropped === 0 && failed === 0) {
        toast.push(
          `${deferred} change${deferred === 1 ? '' : 's'} waiting to sync — will retry`,
        );
      }
    }

    window.addEventListener('sw:update-available', onUpdate);
    window.addEventListener('queue:enqueued', onEnqueued);
    window.addEventListener('queue:drained', onDrained);
    return () => {
      window.removeEventListener('sw:update-available', onUpdate);
      window.removeEventListener('queue:enqueued', onEnqueued);
      window.removeEventListener('queue:drained', onDrained);
    };
  }, [toast]);

  if (!updateReady) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        insetBlockStart: 'env(safe-area-inset-top, 0px)',
        insetInlineStart: 0,
        insetInlineEnd: 0,
        zIndex: 60,
        background: 'var(--color-primary)',
        color: 'var(--color-bg-canvas)',
        padding: 'var(--space-2) var(--space-3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        fontSize: 'var(--text-caption)',
      }}
    >
      <span>New version available.</span>
      <button
        type="button"
        onClick={applyUpdate}
        style={{
          background: 'var(--color-bg-canvas)',
          color: 'var(--color-primary)',
          border: 0,
          borderRadius: 'var(--radius-sm)',
          padding: '6px 12px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  );
}
